import axios from 'axios'
import constant from "@/constant";
import { convertPrefectureCodeToName } from '@/constants/prefecture'
import { trimStartEndOfString } from '@/mixin/helper';
import moment from 'moment';

const regexLangAndCharacters = /^[\u3041-\u3096\u30A0-\u30FF\u3400-\u4DB5\u4E00-\u9FCB\uF900-\uFA6A\u2E80-\u2FD5\uFF5F-\uFF9F\u3000-\u303F\u31F0-\u31FF\u3220-\u3243\u3280-\u337F0-9a-zA-Z\s&~\-@#$^{}()._+=;'`,!%[\]]+$/;

const issuance_request_form = {
  namespaced: true,

  state() {
    return {
      data: {
        id: null,
        status: null,
        status_text: null,
        building_id: null,
        building_house_number: null,
        owner_name: null,
        request_at: new Date(),
        required_document_type: null,
        required_document_id: null,
        required_document_upload_file: null,
        required_document_path: null,
        transfer_notification_date: null,
        transfer_date: moment(new Date()).format('YYYY/MM/DD'),
        issue_documents: [],
        transfer_statement_files: [],
        billing_price: 0,
        issue_expire_date: null,
        reissue_reason: null,
        note: null,
        contact: null,
        tax_price: 0,
        owner_address: null,
        receipt_issued_count: null,
        receipt_issued_at: new Date(),
        receipt_issued_status: null,
        building: {
          name: null,
          name_kana: null,
          postal_code: null,
          address: null,
          building_outline: null,
          management_company_id: null,
          management_company: {
            code: null,
            name: null,
            postal_code: null,
            address1: null,
            address2: null,
            tel: null,
            fax: null,
            use_invoice_number: null,
            invoice_number: null,
            building_search_type: null,
            building_search_word_count: null,
            is_issuance_talk_allowed: null,
            required_document_option: constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN,
            reissue_possible_type: null,
            receipt_stamp: {path: null},
            use_receipt: false,
          },
          management_association_id: null,
        },
        requester: {
          brokerage_company: null,
          requested_by: null,
          address: null,
          licence_type: null,
          licence_number: null,
        },
        issues: [],
        is_postpay_allowed: null,
        payment_method: null,
        management_company_id: null,
        owner_name_explanation: null,
        required_document_explanation: null,
        issue_document_explanation: null,
        whole_explanation: null,
        public_management_id: null,
        isOwner: false,
      },
      buildings: [],
      issueDocuments: [],
      errors: {},
      errorsMulti: [],
      warnings: {},
      form: {
        upload_transferStatement: null,
      },
      talk: {
        messages: [],
        bookmarks: [],
      },
      talk_form: {
        upload_file: null,
        message: '',
      },
    };
  },
  getters: {
    requiredDocumentTypeName(state) {
      return constant.DOCUMENT_TYPE.LIST.find(type => type.value === state.data.required_document_type)?.text ?? '';
    },
    requiredDocumentUploadFileName(state) {
      return state.data.required_document_upload_file?.name ?? '';
    },
    issueDocumentName(state) {
      return state.data.issue_documents.map(issueDocument => issueDocument.name).join('、');
    },
    billingPrice(state) {
      return state.data.issue_documents.reduce((sum, issueDocument) => sum + issueDocument.price, 0);
    },
    transferStatementUploadParameter(state) {
      let form_data = new FormData();
      form_data.append('transfer_statement_file', state.form.upload_transferStatement);
      return form_data;
    },
    formParameter(state) {
      let formData = new FormData();
      formData.append('building_id', state.data.building_id);
      formData.append('building_house_number', state.data.building_house_number);
      formData.append('owner_name', state.data.owner_name);
      formData.append('required_document_type', state.data.required_document_type);
      formData.append('payment_method', state.data.payment_method);
      formData.append('management_company_id', state.data.management_company_id);
      state.data.issue_documents.map(issueDocument => formData.append('issue_document_ids[]', issueDocument.id));
      if (state.data.required_document_upload_file !== null) {
        formData.append('required_document_upload_file', state.data.required_document_upload_file);
      }
      if (state.data.note !== null) {
        formData.append('note', state.data.note);
      }
      formData.append('contact', state.data.contact);
      if (state.isOwner) {
        formData.append('owner_address', state.data.owner_address);
      }
      return formData;
    },
    // upload file chat message
    talkMessage(state) {
      let form_data = new FormData();
      state.talk_form.upload_file && form_data.append('talk_file', state.talk_form.upload_file);
      state.talk_form.message && form_data.append('talk_message', state.talk_form.message);
      return form_data;
    },
  },
  mutations: {
    inputFormItem(state, payload) {
      if (state.data[payload.column] !== undefined) {
        state.data[payload.column] = payload.value;
        if (payload.column === 'building_id') {
          const selectedBuilding = state.buildings.find(building => building.id === payload.value);
          if (selectedBuilding) {
            state.data.building.name = selectedBuilding.name;
            state.data.building.name_kana = selectedBuilding.name_kana;
            state.data.building.postal_code = selectedBuilding.postal_code;
            state.data.building.address = selectedBuilding.address;
            state.data.building.management_association_id = selectedBuilding.management_association_id;
            state.data.building.building_outline = selectedBuilding.building_outline;
          }
        }
      }
    },
    setUploadTransferStatement(state, payload) {
      state.form.upload_transferStatement = payload.value;
    },
    clearUploadTransferStatement(state) {
      state.form.upload_transferStatement = null;
    },
    setBuildings(state, data) {
      state.buildings = data.map(building => ({
        id: building.id,
        name: building.name,
        name_kana: building.name_kana,
        postal_code: building.postal_code,
        address: building.address 
        ? building.address 
        : convertPrefectureCodeToName(building.prefecture_code) + (building.address1 ?? '') + (building.address2 ?? ''),
        building_outline: building.building_outline,
        management_company_id: building.management_company_id,
        management_association_id: building.management_association_id,
      }));
    },
    setBuildingsSelect(state, id) {
      state.data.building_id = id;
      const selectedBuilding = state.buildings.find(building => building.id === id);
      if (selectedBuilding) {
        state.data.building.name = selectedBuilding.name;
        state.data.building.name_kana = selectedBuilding.name_kana;
        state.data.building.postal_code = selectedBuilding.postal_code;
        state.data.building.address = selectedBuilding.address;
        state.data.building.building_outline = selectedBuilding.building_outline;
        state.data.building.management_association_id = selectedBuilding.management_association_id;
      }
    },
    resetBuildingOutline(state) {
      state.data.building.building_outline = null;
    },
    setIssueDocuments(state, data) {
      state.issueDocuments = data.map(issueDocument => ({
        id: issueDocument.id,
        name: issueDocument.name,
        price: issueDocument.price,
        is_required: issueDocument.is_required ?? 0,
        is_default_value: issueDocument.is_default_value ?? 0,
        checked: issueDocument.is_required === 1 || issueDocument.is_default_value === 1 // 初期チェック状態
      }));
      // 今選択済みの発行書類が選択肢から消えていれば、選択解除する
      state.data.issue_documents = state.data.issue_documents.filter(selectedItem => state.issueDocuments.some(item => item.id === selectedItem.id));
      // state.data.idがある、登録済みの場合はissueDocument設定を取得する
      if (Number.isInteger(state.data.id)) {
        // 更新
        const checkedIds = new Set(state.data.issue_documents.map(doc => doc.id));
            state.issueDocuments.forEach(doc => {
          if (checkedIds.has(doc.id)) {
            doc.checked = true; // ID が一致するものだけ checked を true にする
          } else {
            doc.checked = false;
          }
        });
      } else {
        // 新規
        state.data.issue_documents = state.data.issue_documents.filter(
          selectedItem => state.issueDocuments.some(item => item.id === selectedItem.id)
        );
        state.data.issue_documents = state.issueDocuments.filter(doc => doc.checked);
      }     
    },
    updateCheckedDocuments(state, updatedDocuments) {
      // // 更新データを適用
      state.issueDocuments = updatedDocuments.map(doc => ({
        ...doc,
        checked: doc.checked, // ユーザー操作をそのまま適用
      }));
      state.data.issue_documents = state.issueDocuments.filter(doc => doc.checked);
    },
    setPostpay(state, data) {
      state.data.is_postpay_allowed = data[0].is_postpay_allowed;
      state.data.management_company_id = data[0].id;
    },
    setPaymentMethod(state, data) {
      if (data) {
        if (data.length > 0 && data[0].payment_method === constant.PAYMENT_METHOD_CODE.POSTPAY.code
          && state.data.is_postpay_allowed === constant.IS_POSTPAY_ALLOWED_CODE.YES.code) {
          // 後払い
          state.data.payment_method = constant.PAYMENT_METHOD_CODE.POSTPAY.code;
        } else {
          // 前払い
          state.data.payment_method = constant.PAYMENT_METHOD_CODE.PREPAID.code;
        }
      } else {
        // 物件が未選択の場合
        state.data.payment_method = null;
      }
    },
    setBuildingRelatedInfo(state, data) {
      if (data) {
        state.data.owner_name_explanation = data[0].owner_name_explanation;
        state.data.required_document_explanation = data[0].required_document_explanation;
        state.data.issue_document_explanation = data[0].issue_document_explanation;
        state.data.whole_explanation = data[0].whole_explanation;
        state.data.building.management_company.required_document_option = data[0].required_document_option;
        // 申請内容とマスタの不整合を回避する
        switch (state.data.building.management_company.required_document_option) {
          case constant.REQUIRED_DOCUMENT_OPTIONS.REQUIRED:
            if (state.data.required_document_type === constant.DOCUMENT_TYPE.HIDDEN ||
              state.data.required_document_type === constant.DOCUMENT_TYPE.UNNECESSARY || 
              state.data.required_document_type === constant.DOCUMENT_TYPE.ATTACHED_NONE) {
                state.data.required_document_type = null;
            }
            break;
          case constant.REQUIRED_DOCUMENT_OPTIONS.NOT_REQUIRED:
            if (state.data.required_document_type === constant.DOCUMENT_TYPE.HIDDEN) {
                state.data.required_document_type = null;
            }
            break;
          case constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN:
            state.data.required_document_type = constant.DOCUMENT_TYPE.HIDDEN;
            break;
          default:
            break;
        }
      } else {
        state.data.owner_name_explanation = null;
        state.data.required_document_explanation = null;
        state.data.issue_document_explanation = null;
        state.data.whole_explanation = null;
        state.data.building.management_company.required_document_option = constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN;
      }
    },
    setData(state, data) {
      state.data.id = data.id;
      state.data.status = data.status;
      state.data.status_text = data.status_text;
      state.data.building_id = data.building.id;
      state.data.building_house_number = data.building_house_number;
      state.data.owner_name = data.owner_name;
      state.data.request_at = data.request_at;
      state.data.required_document_type = data.required_document_type;
      state.data.required_document_path = data.required_document_path;
      state.data.transfer_notification_date = data.transfer_notification_date;
      state.data.issue_documents = data.issue_documents;
      state.data.transfer_statement_files = data.transfer_statement_files;
      state.data.billing_price = data.billing_price;
      state.data.tax_price = data.tax_price;
      state.data.issue_expire_date = data.issue_expire_date;
      state.data.reissue_reason = data.reissue_reason;
      state.data.note = data.note;
      state.data.contact = data.contact;
      state.data.payment_method = data.payment_method;
      state.data.receipt_issued_count = data.receipt_issued_count;
      state.data.receipt_issued_at = data.receipt_issued_at;
      state.data.receipt_issued_status = data.receipt_issued_status;
      state.data.building.name = data.building.name;
      state.data.building.name_kana = data.building.name_kana;
      state.data.building.postal_code = data.building.postal_code;
      state.data.building.address = data.building.address;
      state.data.building.management_company_id = data.management_company_id;
      state.data.building.management_company.code = data.building.management_company.code;
      state.data.owner_address = data.requester?.address ?? '';
      state.data.building.management_company_id = data.management_company_id;
      state.data.building.management_company.code = data.building.management_company.code;
      state.data.building.management_company.name = data.building.management_company.name;
      state.data.building.management_company.postal_code = data.building.management_company.postal_code;
      state.data.building.management_company.address1 = data.building.management_company.address1;
      state.data.building.management_company.address2 = data.building.management_company.address2;
      state.data.building.management_company.tel = data.building.management_company.tel;
      state.data.building.management_company.fax = data.building.management_company.fax;
      state.data.building.management_company.use_invoice_number = data.building.management_company.use_invoice_number;
      state.data.building.management_company.invoice_number = data.building.management_company.invoice_number;
      state.data.building.management_company.building_search_type = data.building.management_company.building_search_type;
      state.data.building.management_company.building_search_word_count = data.building.management_company.building_search_word_count;
      state.data.building.management_company.is_issuance_talk_allowed = data.building.management_company.is_issuance_talk_allowed;
      state.data.building.management_company.required_document_option = data.building.management_company.required_document_option;
      state.data.building.management_company.reissue_possible_type = data.building.management_company.reissue_possible_type;
      state.data.building.management_company.receipt_stamp = data.building.management_company.receipt_stamp;
      state.data.building.management_company.use_receipt = data.building.management_company.use_receipt;
      state.data.building.management_association_id = data.building.management_association_id;
      state.data.requester.brokerage_company = data.requester.brokerage_company;
      state.data.requester.requested_by = data.requester.requested_by;
      state.data.requester.address = data.requester.address;
      state.data.requester.licence_type = data.requester.licence_type;
      state.data.requester.licence_number = data.requester.licence_number;
      state.data.owner_name_explanation = data.owner_name_explanation;
      state.data.required_document_explanation = data.required_document_explanation;
      state.data.issue_document_explanation = data.issue_document_explanation;
      state.data.whole_explanation = data.whole_explanation;
      state.data.issues = data.issues;
      state.data.public_management_id = data.building.management_company_id;
      state.talk_form = {
        upload_file: null,
        message: '',
      };
      state.errors = {};
    },
    resetData(state) {
      state.data.id = null;
      state.data.status = null;
      state.data.status_text = null;
      state.data.building_id = null;
      state.data.building_house_number = null;
      state.data.owner_name = null;
      state.data.request_at = new Date();
      state.data.required_document_type = null;
      state.data.required_document_upload_file = null;
      state.data.required_document_path = null;
      state.data.required_document_id = null;
      state.data.transfer_notification_date = null;
      state.data.issue_documents = [];
      state.data.transfer_statement_files = [];
      state.data.billing_price = 0;
      state.data.issue_expire_date = null;
      state.data.reissue_reason = null;
      state.data.note = null;
      state.data.contact = null;
      state.data.payment_method = null;
      state.data.tax_price = 0;
      state.data.issue_expire_date = null;
      state.data.reissue_reason = null;
      state.data.note = null;
      state.data.owner_address  = null;
      state.data.contact = null;
      state.data.payment_method = null;
      state.data.receipt_issued_count = null;
      state.data.receipt_issued_at = new Date();
      state.data.receipt_issued_status = null;
      state.data.building.name = null;
      state.data.building.name_kana = null;
      state.data.building.postal_code = null;
      state.data.building.address = null;
      state.data.building.building_outline = null;
      state.data.building.management_company_id = null;
      state.data.building.management_company.code = null;
      state.data.building.management_company.name = null;
      state.data.building.management_company.code = null;
      state.data.building.management_company.postal_code = null;
      state.data.building.management_company.address1 = null;
      state.data.building.management_company.address2 = null;
      state.data.building.management_company.tel = null;
      state.data.building.management_company.fax = null;
      state.data.building.management_company.use_invoice_number = null;
      state.data.building.management_company.invoice_number = null;
      state.data.building.management_company.building_search_type = null;
      state.data.building.management_company.building_search_word_count = null;
      state.data.building.management_company.is_issuance_talk_allowed = null;
      state.data.building.management_company.required_document_option = constant.REQUIRED_DOCUMENT_OPTIONS.HIDDEN;
      state.data.building.management_company.reissue_possible_type = null;
      state.data.building.management_company.receipt_stamp = null;
      state.data.building.management_company.use_receipt = false;
      state.data.building.management_association_id = null;
      state.data.requester.brokerage_company = null;
      state.data.requester.requested_by = null;
      state.data.requester.address = null;
      state.data.requester.licence_type = null;
      state.data.requester.licence_number = null;
      state.data.owner_name_explanation = null;
      state.data.required_document_explanation = null;
      state.data.issue_document_explanation = null;
      state.data.whole_explanation = null;
      state.data.public_management_id = null;
      state.data.transfer_date = null
      state.data.issues = [];
      state.buildings = [];
      state.issueDocuments = [];
      state.errorsMulti = [];
      state.talk_form = {
        upload_file: null,
        message: '',
      };
      state.talk.messages = [];
      state.talk.bookmarks = [];
      state.paymentMethodList = [];
      state.errors = {};
    },
    setIssuanceRequestId(state, data) {
      state.data.id = data;
    },
    setRequiredDocumentId(state, data) {
      state.data.required_document_id = data;
    },
    setErrors(state, errors) {
      state.errors = { ...errors };
      state.errorsMulti = [...state.errorsMulti, errors];
      if (errors === false) {
        return state.errorsMulti = [{ transfer_statement_file: ['ファイルは５つずつ選択してください。'] }];
      }
      if (state.errors.issue_document_ids) {
        state.errors.issue_documents = state.errors.issue_document_ids;
        delete state.errors.issue_document_ids;
      }
    },
    setError(state, payload) {
      state.errors[payload.key] = payload.value;
    },
    resetErrors(state) {
      state.errors = {};
      state.errorsMulti = [];
    },
    setWarning(state, payload) {
      state.warnings[payload.key] = payload.value;
    },
    setTransferDate(state,payload){
      state.data.transfer_date = moment(payload).format('YYYY/MM/DD');
    },
    resetWarnings(state) {
      state.warnings = {};
    },
    setContact(state, contact) {
      if (contact !== undefined) {
        state.data.contact = contact;
      }
    },
    setTalkUploadFile(state, payload) {
      state.talk_form.upload_file = payload.value;
    },
    resetTalkUploadFile(state) {
      state.talk_form.upload_file = null;
    },
    setTalkMessage(state, payload) {
      state.talk_form.message = trimStartEndOfString(payload.value);
    },
    resetTalkMessage(state) {
      state.talk_form.message = '';
    },
    setTalkMessageData(state, data) {
      state.talk.messages = data.data;
      state.talk.bookmarks = data.data.filter((talk) => talk.bookmark);
    },
    setIsOwner(state, value) {
      state.isOwner = value;
    },  
  },
  actions: {
    load({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        axios.get('/api/issuance_requests/' + payload.id).then((response) => {
          if (payload?.edit && response.data.data.status !== constant.ISSUANCE_REQUEST_STATUS.MODIFICATION_REQUEST.code) {
            reject({ conflict: true });
          } else {
            commit('setData', response.data.data);
            commit('setBuildings', [response.data.data.building]);
            if (payload.edit) {
              const params = {
                building_id: response.data.data.building.id,
                is_guest: payload.is_guest
              };
              dispatch('loadIssueDocuments', { params }).then(() => {
                resolve(true);
              });
            } else {
              resolve(true);
            }
          }
        }).catch(() => reject());
      });
    },
    loadTransferDate({commit},payload){
        commit('setTransferDate', moment(payload).format('YYYY/MM/DD'));
    },

    loadMe({ commit }, payload) {
      return new Promise((resolve, reject) => {
        axios.get('/api/issuance_requests/me/' + payload.id).then((response) => {
          commit('setData', response.data.data);
          resolve(true);
        }).catch(() => reject());
      });
    },

    loadBuildings({ commit, dispatch }, payload) {
      commit('resetWarnings');
      return new Promise((resolve, reject) => {
        const params = {
          management_company_code: payload.management_company_code,
          name: payload.name,
          name_kana: payload.name_kana,
          prefecture_code: payload.prefecture_code,
          address1: payload.address1,
          requestable: 1,
          per_page: constant.ISSUANCE_REQUEST_MAX_BUILDING_LIST_ITEMS,
        };
        axios.get('/api/buildings', { params }).then((response) => {
          if (response.data.data.length !== 0) {
            commit('setBuildings', response.data.data);
            if (response.data.meta.total > constant.ISSUANCE_REQUEST_MAX_BUILDING_LIST_ITEMS) {
              commit('setWarning', { key: 'building_id', value: ['物件が多すぎるためすべてを表示できません。絞込を実行してください。'] });
            }
            if (response.data.data.length == 1) {
              commit('setBuildingsSelect', response.data.data[0].id);
              dispatch('loadIssueDocuments', { 
                building_id: response.data.data[0].id,
                is_guest: payload.is_guest
              }).then(() => {
                resolve(true);
              }).catch((error) => reject(error));
            }
            resolve(true);
          } else {
            reject({ handled: true, notFound: true });
          }
        }).catch((error) => {
          const reason = {
            handled: false,
          };
          if (error.response.status === 422) {
            reason.errors = error.response.data.errors;
          }
          reject(reason);
        });
      });
    },

    loadIssueDocuments({ state, commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        const building_id = payload.building_id ?? payload.params.building_id;
        const building = state.buildings.find(building => building_id === building.id);
        const is_guest = payload.is_guest ?? payload.params.is_guest;
        const params = {
          management_company_id: building.management_company_id,
          management_association_id: building.management_association_id,
        };
        axios.get('/api/issue_documents', { params }).then((response) => {
          if (response.data.data.length !== 0) {
            commit('setIssueDocuments', response.data.data);
            // 管理会社の情報を取得
            dispatch('loadManagementCompany', {
              management_company_id: building.management_company_id,
              is_guest: is_guest
            }).then(() => {
              resolve(true);
            }).catch((error) => reject(error));            
          } else {
            commit('setIssueDocuments', []);
            reject({ handled: true, notFound: true });
          }
        }).catch((error) => {
          const reason = {
            handled: false,
          };
          if (error.response.status === 422) {
            reason.errors = error.response.data.errors;
          }
          reject(reason);
        });
      });
    },

    // 発行書類を取得後、管理会社の支払方法を取得⇒ユーザの初期表示取得
    loadManagementCompany({ commit, dispatch }, params) {
      return new Promise((resolve, reject) => {
        axios.get(`/api/management_companies/${params.management_company_id}`).then((response) => {
          commit('setBuildingRelatedInfo', response.data.data);
          if (params.is_guest) {
            // ゲストの場合、前払い固定
            commit('setPaymentMethod', response.data.data);
            resolve(true);
          } else {
            commit('setPostpay', response.data.data);
            if (response.data.data[0].is_postpay_allowed === constant.IS_POSTPAY_ALLOWED_CODE.YES.code) {
              // 後払い可能
              dispatch('loadPaymentMethod', { management_company_id: params.management_company_id }).then(() => {
                resolve(true);
              }).catch((error) => reject(error));
            } else {
              // 後払い不可
              commit('setPaymentMethod', response.data.data);
              resolve(true);
            }
          }
        }).catch((error) => {
          const reason = {
            handled: false,
          };
          if (error.response.status === 422) {
            reason.errors = error.response.data.errors;
          }
          reject(reason);
        });
      });
    },

    loadPaymentMethod({ commit }, params) {
      return new Promise((resolve, reject) => {
        axios.get('/api/issuance_requests/paymentmethod', { params: { management_company_id: params.management_company_id } }).then((response) => {
          commit('setPaymentMethod', response.data.data);
          resolve(true);
        }).catch((error) => {
          const reason = {
            handled: false,
          };
          if (error.response.status === 422) {
            reason.errors = error.response.data.errors;
          }
          reject(reason);
        });
      });
    },

    loadTalkMessage({ commit }, payload) {
      commit('resetTalkMessage');
      commit('resetTalkUploadFile');
      return new Promise((resolve, reject) => {
        axios.get(`/api/talks/${payload.issuanceRequest}`).then((response) => {
          commit('setTalkMessageData', { data: response.data.data });
          resolve(true);
        }).catch((error) => {
          reject(getFirstErrorMessage(error.response.data.errors));
        });
      });
    },

    register({ getters, commit }) {
      return new Promise((resolve, reject) => {
        axios.post('/api/issuance_requests', getters.formParameter).then((response) => {
          commit('setIssuanceRequestId', response.data.data.id);
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', error.response.data.errors);
            handled = true;
          }
          reject({ handled });
        });
      });
    },

    update({ state, getters, commit }) {
      return new Promise((resolve, reject) => {
        // PUTではファイルのアップロードができないため、POSTで送信しつつOverrideする
        const headers = {
          'X-HTTP-Method-Override': 'PUT',
        };
        axios.post('/api/issuance_requests/' + state.data.id, getters.formParameter, { headers }).then(() => {
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', error.response.data.errors);
            handled = true;
          }
          reject({ handled });
        });
      });
    },

    updateReceiptStatus({ state, commit }, payload){
      return new Promise((resolve, reject) => {
        axios.put('/api/issuance_requests/receipt_status/' + state.data.id, payload).then(() => {
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', error.response.data.errors);
            handled = true;
          }
          reject({ handled });
        });
      });
    },
    validate({ state, commit }) {
      return new Promise((resolve, reject) => {
        commit('resetErrors');
        if (!state.data.building_id) {
          commit('setError', { key: 'building_id', value: ['依頼物件の選択は必須です。'] });
        }
        if (!state.data.building_house_number) {
          commit('setError', { key: 'building_house_number', value: ['依頼物件住戸番号の入力は必須です。'] });
        }
        if (state.data.building_house_number && state.data.building_house_number.length > 10) {
          commit('setError', { key: 'building_house_number', value: ['依頼物件住戸番号は、10文字以下で指定してください。'] });
        }
        if (!state.data.owner_name) {
          commit('setError', { key: 'owner_name', value: ['所有者の入力は必須です。'] });
        }
        if (state.data.owner_name && state.data.owner_name.length > 60) {
          commit('setError', { key: 'owner_name', value: ['所有者は、60文字以下で指定してください。'] });
        }
        if (state.data.required_document_type === null) {
          commit('setError', { key: 'required_document_type', value: ['依頼主確認書類の選択は必須です。'] });
        } else if (state.data.required_document_type !== constant.DOCUMENT_TYPE.HIDDEN
          && state.data.required_document_type !== constant.DOCUMENT_TYPE.UNNECESSARY
          && state.data.required_document_type !== constant.DOCUMENT_TYPE.ATTACHED_NONE) {
          if (!state.data.required_document_upload_file && !state.data.required_document_path) {
            commit('setError', { key: 'required_document_upload_file', value: ['ファイル選択は必須です。'] });
          }
        }
        if (state.data.issue_documents.length === 0) {
          commit('setError', { key: 'issue_documents', value: ['発行書類の選択は必須です。'] });
        }
        if (state.isOwner && !state.data.owner_address) {
          commit('setError', { key: 'owner_address', value: ['依頼者住所の入力は必須です。'] });
        }
        if (state.data.required_document_upload_file) {
          validateFileType(state.data.required_document_upload_file).catch(() => {
            commit('setError', {
              key: 'required_document_upload_file',
              value: ['ファイルは.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpeg,.jpg形式を指定してください。']
            });
          });
        }
        if (state.data.required_document_upload_file && state.data.required_document_upload_file.size > 10485760
        ) {
          commit('setError', { key: 'required_document_upload_file', value: ['ファイルサイズは10MB以下に設定してください。'] });
        }
        if (state.data.note && state.data.note.length > 5000) {
          commit('setError', { key: 'note', value: ['備考は、5000文字以下で指定してください。'] });
        }
        if (!state.data.contact) {
          commit('setError', { key: 'contact', value: ['連絡先の入力は必須です。'] });
        }
        if (state.data.contact && state.data.contact.length > 200) {
          commit('setError', { key: 'contact', value: ['連絡先は、200文字以下で指定してください。'] });
        }

        if (Object.keys(state.errors).length === 0) {
          resolve(true);
        } else {
          reject(state.errors);
        }
      });
    },

    deleteTransferStatement({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        axios.delete(`/api/issuance_requests/${state.data.id}/transfer_statement/${payload.id}`).then(() => {
          commit('resetErrors');
          commit('setFlashMessage', { value: '削除しました' }, { root: true });
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', error.response.data.errors);
            handled = true;
          }
          reject({ handled });
        });
      });
    },

    uploadTransferStatement({ state, getters, commit }) {
      return new Promise((resolve, reject) => {
        axios.post(`/api/issuance_requests/${state.data.id}/transfer_statement`, getters.transferStatementUploadParameter).then(() => {
          commit('resetErrors');
          commit('setFlashMessage', { value: '登録しました' }, { root: true });
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', {transfer_statement_files: error.response.data.errors.transfer_statement_file});
            handled = true;
          }
          reject({ handled });
        });
      });
    },

    multiUploadTransferStatement({ state, commit }, payload) {
      if (payload === false) {
        commit('setErrors', payload);
      } else {
        let form_data = new FormData();
        form_data.append('transfer_statement_file', payload);
        return new Promise((resolve, reject) => {
          axios.get('/sanctum/csrf-cookie').then(() => {
            axios
              .post(`/api/issuance_requests/${state.data.id}/transfer_statement`, form_data)
              .then(() => {
                if (this.transfer_date != null) {
                    commit('setFlashMessage', { value: '登録しました' }, { root: true });
                }
                commit('setFlashMessage', { value: '登録しました' }, { root: true });
                resolve(true);
              })
              .catch((error) => {
                let handled = false;
                if (error.response.status === 422) {
                  commit('setErrors', { transfer_statement_file: error.response.data.errors.transfer_statement_file, name: payload?.name });
                  handled = true;
                }
                reject({ handled });
              });
            }).catch((error) => { reject({ error })});
        });
      }
    },

    transferComplete({ state, commit }) {
      return new Promise((resolve, reject) => {
        axios.put(`/api/issuance_requests/${state.data.id}/transfer_complete`, state.data).then(() => {
          commit('clearUploadTransferStatement');
          resolve(true);
        }).catch((error) => {
          let handled = false;
          if (error.response.status === 422) {
            commit('setErrors', error.response.data.errors);
            handled = true;
          }
          reject({ handled });
        });
      });
    },

    operate({ state, commit }, payload) {
      return new Promise((resolve, reject) => {
        axios.put(`/api/issuance_requests/${state.data.id}/operate_applicant`, {
          operation: payload.operation,
          notificationReason: payload.notificationReason,
        }).then((response) => {
          commit('setData', response.data.data);
          commit('setFlashMessage', { value: '更新しました' }, { root: true });
          resolve(true);
        }).catch((error) => {
          let message = null;
          if (error.response.status === 409 && error.response.data.message.length > 0) {
            message = error.response.data.message
          }
          reject(message)
        });
      });
    },

    sendTalkMesssage({ getters, commit }, payload) {
      return new Promise((resolve, reject) => {
        axios.post(`/api/talks/${payload.issuanceRequest}`, getters.talkMessage).then(() => {
          commit('resetTalkMessage');
          commit('resetTalkUploadFile');
          resolve(true);
        }).catch((error) => {
          reject(getFirstErrorMessage(error.response.data.errors));
        });
      });
    },

    downloadTalkFile(_ , payload) {
      return new Promise((resolve, reject) => {
        axios.post(`/api/talks/${payload.issuanceRequest}/${payload.uuid}/download`).then((response) => {
          let base64 = response.data.data.file;
          let bin = atob(base64.replace(/^.*,/, ''));
          let buffer = new Uint8Array(bin.length);
          for (var i = 0; i < bin.length; i++) {
            buffer[i] = bin.charCodeAt(i);
          }
          const blob = new Blob([buffer.buffer], { type: response.data.data.content_type });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = response.data.data.file_name;
          link.click();
          link.remove();
          resolve(true);
        }).catch((error) => {
          reject(getFirstErrorMessage(error.response.data.errors));
        });
      });
    },

    updateBookmark(_, payload) {
      return new Promise((resolve, reject) => {
        axios.put(`/api/talk_bookmarks/${payload.issuanceRequest}/${payload.issuanceRequestTalk}`,
         { bookmark: payload.bookmark, })
        .then(() => {
          resolve(true);
        }).catch((error) => {
          reject(getFirstErrorMessage(error.response.data.errors));
        });
      });
    },

    deleteTalk(_, payload) {
      return new Promise((resolve, reject) => {
        axios.delete(`/api/talks/${payload.issuanceRequest}/${payload.issuanceRequestTalk}`).then(() => {
          resolve(true);
        }).catch((error) => {
          reject(getFirstErrorMessage(error.response.data.errors));
        });
      });
    },

    validateTalkFile(_, payload) {
      return new Promise((resolve, reject) => {
        const pos = payload.file.name.lastIndexOf('.');
        const name = payload.file.name.slice(0, pos).toLowerCase();
        if (name.length > 255) 
          return reject('ファイル名は、255文字以下で指定してください。');
        // check size over 10MB
        if (payload.file.size > 10 * 1024 * 1024) {
          return reject('10MBを超えるファイルはアップロードできません。');
        }
        if (!regexLangAndCharacters.test(name)) {
          return reject('ファイル名に使用できない文字が含まれています。')
        }
        validateFileType(payload.file).catch(() => {
          return reject('ファイルは.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpeg,.jpg形式を指定してください。');
        }).then(() => resolve())
      })
    }
  }
};

function getFirstErrorMessage(error) {
  if (!Object.keys(error)?.length) return '';
  const firstErrorKey = Object.keys(error)[0];
  return error[firstErrorKey][0];
}

function validateFileType(blob) {
  return new Promise((resolve, reject) => {
    // 拡張子を確認
    const allowExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpeg', 'jpg'];

    let pos = blob.name.lastIndexOf('.');
    if (pos === -1) reject();

    let ext = blob.name.slice(pos + 1).toLowerCase();
    if (allowExtensions.indexOf(ext) === -1) reject();

    // ファイルのバイナリヘッダを確認
    var fileReader = new FileReader();
    fileReader.onloadend = function (e) {
      var arr = (new Uint8Array(e.target.result)).subarray(0, 4);
      var header = "";
      for (var i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      const allow_types = [
        "ffd8ff",     // JPG
        "89504e47",   // PNG
        "25504446",   // PDF
        "504b34",     // DOCX、XLSX、PPTX
        "d0cf11e0",   // DOC、XLS、PPT
      ];
      let canAccept = allow_types.find(type => header.indexOf(type) > -1);

      if (canAccept) resolve();
      reject();
    };
    fileReader.readAsArrayBuffer(blob);
  });
}

export default issuance_request_form;
